import { unref, type ComputedRef, type Ref } from 'vue';
import type { QueryClient } from '@tanstack/vue-query';
import getAccountShipmentRoutesBatch from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { ShipmentFormChangeSet } from '@/composables/shipment-form-change-set';
import { changeSetFromSavePlan } from '@/composables/shipment-form-change-set';
import type { ShipmentFormPersistDeps } from '@/composables/shipment-form-persist-deps';
import type { RoutePersistIntent } from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';
import { putFormStopsOnly } from '@/components/shipment/form/utils/persist/persistFormStops';
import {
  syncFormRoutes,
  type RouteSyncMode,
} from '@/components/shipment/form/utils/persist/persistFormRoutes';
import { publishFormShipment } from '@/components/shipment/form/utils/persist/persistFormDevices';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';
import type { ShipmentSavePlan } from '@/components/shipment/form/save/shipment-save-plan';

export type ShipmentFormSaveMode =
  | 'create-publish'
  | 'edit-save'
  | 'edit-publish';

export type ShipmentFormSaveOrchestratorOptions = {
  accountId: Ref<number> | ComputedRef<number>;
  shipmentId: Ref<number> | ComputedRef<number | null>;
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  routingStrategy?: Ref<RoutingStrategy> | ComputedRef<RoutingStrategy>;
  deviceIds?: Ref<readonly number[]> | ComputedRef<readonly number[]>;
  canEditMetadata: boolean;
  canEditRoute: boolean;
  canEditTracker: boolean;
  metadataPayload: {
    name: string;
    carrier: string | null;
    notes: string | null;
    asset_value: number | null;
  };
  deviceIdsToAssign: readonly number[];
};

export type ShipmentFormSaveRunInput = {
  mode: ShipmentFormSaveMode;
  plan: ShipmentSavePlan;
  /** @deprecated Derived from plan when omitted. */
  changeSet?: ShipmentFormChangeSet;
  routeIntent: RoutePersistIntent | null;
  deviceIdsOverride?: readonly number[];
  suspendHydration?: boolean;
  deferInvalidation?: boolean;
  includePublish?: boolean;
};

type SaveStepContext = {
  deps: ShipmentFormPersistDeps;
  options: ShipmentFormSaveOrchestratorOptions;
  input: ShipmentFormSaveRunInput;
  plan: ShipmentSavePlan;
};

type SaveStep = {
  id: string;
  when: (plan: ShipmentSavePlan) => boolean;
  run: (ctx: SaveStepContext) => Promise<void>;
};

async function saveMetadataStep(ctx: SaveStepContext): Promise<void> {
  if (ctx.plan.metadata === 'skip' || !ctx.options.canEditMetadata) {
    return;
  }

  const sid = unref(ctx.options.shipmentId);
  if (sid == null) throw new Error('Missing shipment id');

  const payload = ctx.options.metadataPayload;
  await ctx.deps.patchShipment({
    accountId: unref(ctx.options.accountId),
    shipmentId: sid,
    payload,
  });

  const formStore = useShipmentFormStore();
  formStore.markMetadataPersisted(
    payload.name,
    payload.carrier,
    payload.notes,
    payload.asset_value,
  );
}

async function saveStopsStep(ctx: SaveStepContext): Promise<void> {
  if (ctx.plan.stops.method === 'skip' || !ctx.options.canEditRoute) {
    return;
  }

  const sid = unref(ctx.options.shipmentId);
  if (sid == null) throw new Error('Missing shipment id');

  await putFormStopsOnly(ctx.deps, {
    accountId: ctx.options.accountId,
    shipmentId: ctx.options.shipmentId,
    stopsPort: ctx.options.stopsPort,
    deferInvalidation: ctx.input.deferInvalidation !== false,
    stopWrite: ctx.plan.stops,
  });
}

async function saveRoutesStep(ctx: SaveStepContext): Promise<void> {
  const { routeIntent } = ctx.input;
  if (
    routeIntent == null ||
    ctx.plan.routes.mode === 'skip' ||
    !ctx.options.canEditRoute
  ) {
    return;
  }

  const sid = unref(ctx.options.shipmentId);
  if (sid == null) throw new Error('Missing shipment id');

  const serverRoutesAlreadyCleared =
    ctx.plan.routes.mode === 'recreate' &&
    ctx.plan.routes.serverClearsRoutes &&
    ctx.plan.stops.method !== 'skip';

  await syncFormRoutes(
    ctx.deps,
    {
      accountId: ctx.options.accountId,
      shipmentId: ctx.options.shipmentId,
      stopsPort: ctx.options.stopsPort,
      routingStrategy: ctx.options.routingStrategy,
      deviceIds: ctx.options.deviceIds,
    },
    {
      mode: ctx.plan.routes.mode as RouteSyncMode,
      intent: routeIntent,
      deferInvalidation: ctx.input.deferInvalidation !== false,
      serverRoutesAlreadyCleared,
      deviceIdsOverride: ctx.input.deviceIdsOverride
        ? [...ctx.input.deviceIdsOverride]
        : undefined,
    },
  );
}

async function saveDevicesStep(ctx: SaveStepContext): Promise<void> {
  if (ctx.plan.devices === 'skip' || !ctx.options.canEditTracker) {
    return;
  }

  const sid = unref(ctx.options.shipmentId);
  if (sid == null) throw new Error('Missing shipment id');

  const deviceIds = [...ctx.options.deviceIdsToAssign];
  if (!deviceIds.length) {
    throw new Error('Tracker is required');
  }

  await ctx.deps.postDevices({
    accountId: unref(ctx.options.accountId),
    shipmentId: sid,
    payload: { device_ids: deviceIds },
  });

  const formStore = useShipmentFormStore();
  formStore.markDeviceIdsPersisted(deviceIds);
}

async function publishStep(ctx: SaveStepContext): Promise<void> {
  if (!ctx.plan.publish && !ctx.input.includePublish) return;

  const sid = unref(ctx.options.shipmentId);
  if (sid == null) throw new Error('Missing shipment id');

  await publishFormShipment(
    { publishShipment: ctx.deps.publishShipment },
    {
      accountId: ctx.options.accountId,
      shipmentId: ctx.options.shipmentId,
    },
  );
}

export const SHIPMENT_FORM_SAVE_STEPS: SaveStep[] = [
  {
    id: 'metadata',
    when: (plan) => plan.metadata === 'patch',
    run: saveMetadataStep,
  },
  {
    id: 'stops',
    when: (plan) => plan.stops.method !== 'skip',
    run: saveStopsStep,
  },
  {
    id: 'routes',
    when: (plan) => plan.routes.mode !== 'skip',
    run: saveRoutesStep,
  },
  {
    id: 'devices',
    when: (plan) => plan.devices === 'post',
    run: saveDevicesStep,
  },
  {
    id: 'publish',
    when: (plan) => plan.publish,
    run: publishStep,
  },
];

export async function invalidateShipmentFormQueries(
  queryClient: QueryClient,
  accountId: number,
  shipmentId: number,
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['account-shipments'] });
  await queryClient.invalidateQueries({
    queryKey: getAccountShipmentV2.queryKey(accountId, shipmentId),
  });
  await queryClient.invalidateQueries({
    queryKey: getAccountShipmentRoutesBatch.queryKey(accountId, [shipmentId]),
  });
}

export async function executeShipmentFormSave(
  deps: ShipmentFormPersistDeps,
  options: ShipmentFormSaveOrchestratorOptions,
  input: ShipmentFormSaveRunInput,
): Promise<void> {
  const formStore = useShipmentFormStore();
  const suspendHydration = input.suspendHydration !== false;
  const plan = input.plan;
  const ctx: SaveStepContext = { deps, options, input, plan };

  if (suspendHydration) {
    formStore.suspendFormHydration();
  }

  try {
    for (const step of SHIPMENT_FORM_SAVE_STEPS) {
      if (!step.when(plan)) continue;
      await step.run(ctx);
    }

    const sid = unref(options.shipmentId);
    if (sid != null && input.deferInvalidation !== false) {
      await invalidateShipmentFormQueries(
        deps.queryClient,
        unref(options.accountId),
        sid,
      );
    }
  } finally {
    if (suspendHydration) {
      formStore.resumeFormHydration();
    }
  }
}

export { changeSetFromSavePlan };
